/**
 * CategoryBudgetCard Component
 *
 * 카테고리별 예산 설정을 위한 입력 카드 컴포넌트
 *
 * 책임:
 * - 대분류 예산 금액 입력
 * - 소분류 예산 입력 (접기/펼치기)
 * - 소분류 합계 계산 및 검증
 * - 대분류 예산 초과 시 에러 표시
 */

import type { Category } from '@/domains/category/types/Category'

/**
 * CategoryBudgetCard Props
 */
export interface CategoryBudgetCardProps {
  /** 카테고리 정보 */
  category: Category
  /** 대분류 예산 금액 */
  value: number
  /** 대분류 예산 변경 핸들러 */
  onChange: (value: number) => void
  /** 소분류 카테고리 목록 */
  minorCategories: Category[]
  /** 소분류 예산 금액 맵 (minorId -> amount) */
  minorValues: Record<string, number>
  /** 소분류 예산 변경 핸들러 */
  onMinorChange: (minorId: string, value: number) => void
  /** 소분류 펼침 상태 */
  expanded: boolean
  /** 펼침 토글 핸들러 */
  onToggle: () => void
}

/**
 * 카테고리별 예산 설정 카드
 *
 * 대분류 예산 입력 + 소분류 예산 입력(접기/펼치기)을 제공
 * 소분류 합계가 대분류를 초과하면 에러 표시
 */
export function CategoryBudgetCard({
  category,
  value,
  onChange,
  minorCategories,
  minorValues,
  onMinorChange,
  expanded,
  onToggle,
}: CategoryBudgetCardProps) {
  /**
   * 소분류 합계 계산
   */
  const minorSum = minorCategories
    .filter(m => minorValues[m.id])
    .reduce((sum, m) => sum + (minorValues[m.id] || 0), 0)

  /**
   * 검증: 소분류 합계가 대분류를 초과하는지
   */
  const hasError = value > 0 && minorSum > value

  /**
   * 숫자 포맷팅
   */
  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('ko-KR')
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
      {/* 대분류 입력 */}
      <div className="flex items-center gap-3 mb-3">
        <span className="text-2xl">{category.icon}</span>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{category.name}</h3>
          <input
            type="number"
            inputMode="numeric"
            value={value || ''}
            onChange={e => onChange(parseInt(e.target.value) || 0)}
            placeholder="예산 금액 (원)"
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
              hasError
                ? 'border-red-300 focus:ring-red-500'
                : 'border-gray-300 focus:ring-primary-500 dark:focus:ring-primary-400'
            }`}
          />
          {hasError && (
            <p className="text-xs text-red-600 mt-1">
              소분류 합계가 대분류를 초과합니다
            </p>
          )}
        </div>
      </div>

      {/* 소분류 접기/펼치기 버튼 */}
      {minorCategories.length > 0 && (
        <button
          onClick={onToggle}
          className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 mb-2 flex items-center gap-1"
        >
          <span>{expanded ? '▼' : '▶'}</span>
          <span>소분류 설정 ({minorCategories.length}개)</span>
        </button>
      )}

      {/* 소분류 목록 */}
      {expanded && minorCategories.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-2">
          {minorCategories.map(minor => (
            <div key={minor.id} className="flex items-center gap-2">
              <span className="text-lg">{minor.icon}</span>
              <span className="text-sm flex-1 text-gray-700 dark:text-gray-200">{minor.name}</span>
              <input
                type="number"
                inputMode="numeric"
                value={minorValues[minor.id] || ''}
                onChange={e =>
                  onMinorChange(minor.id, parseInt(e.target.value) || 0)
                }
                placeholder="0"
                className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
              />
            </div>
          ))}

          {/* 합계 표시 */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">소분류 합계:</span>
              <span
                className={
                  hasError
                    ? 'text-red-600 font-semibold'
                    : 'text-gray-900 dark:text-gray-100 font-medium'
                }
              >
                {formatCurrency(minorSum)}원
              </span>
            </div>
            {value > 0 && (
              <div className="flex justify-between mt-1 text-xs">
                <span className="text-gray-500 dark:text-gray-400">대분류 예산:</span>
                <span className="text-gray-700 dark:text-gray-200">{formatCurrency(value)}원</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
